import { formatBrlCurrency } from "@/lib/price";
import { MAX_CART_ITEM_QUANTITY } from "@/lib/cartStorage";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { ImageIcon, Minus, Plus, X } from "lucide-react";

function CartItemThumbnail({ url, name }: { url?: string | null; name: string }) {
  return (
    <div className="size-12 shrink-0 overflow-hidden border border-primary-200 bg-primary-100 dark:border-primary-800 dark:bg-primary-900">
      {url ? (
        <img src={url} alt={name} className="size-full object-cover" />
      ) : (
        <div
          className="flex size-full items-center justify-center text-primary-400"
          role="img"
          aria-label={`Sem imagem para ${name}`}
        >
          <ImageIcon className="size-4" aria-hidden />
        </div>
      )}
    </div>
  );
}

function QuantityStepper({
  productId,
  name,
  quantity,
}: {
  productId: string;
  name: string;
  quantity: number;
}) {
  const { updateQuantity } = useCart();

  return (
    <div className="flex items-center gap-1 shrink-0">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7"
        onClick={() => updateQuantity(productId, quantity - 1)}
        aria-label={`Diminuir quantidade de ${name}`}
      >
        <Minus className="size-3" />
      </Button>
      <span
        className="min-w-[1.5rem] text-center text-sm font-light text-primary-900 dark:text-white"
        aria-label={`Quantidade: ${quantity}`}
      >
        {quantity}
      </span>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7"
        disabled={quantity >= MAX_CART_ITEM_QUANTITY}
        onClick={() => updateQuantity(productId, quantity + 1)}
        aria-label={`Aumentar quantidade de ${name}`}
      >
        <Plus className="size-3" />
      </Button>
    </div>
  );
}

export default function CartSummary() {
  const { items, removeItem, totalCents } = useCart();

  if (items.length === 0) {
    return (
      <p className="text-sm font-light text-primary-600 dark:text-primary-400 py-4">
        Seu carrinho está vazio
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <ul className="space-y-3">
        {items.map((item) => {
          const lineTotalCents = item.priceCents * item.quantity;

          return (
            <li
              key={item.productId}
              className="flex items-start gap-3 border-b border-primary-100 dark:border-primary-800 pb-3"
            >
              <CartItemThumbnail url={item.thumbnailUrl} name={item.name} />
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-light text-primary-900 dark:text-white truncate">
                    {item.name}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-8 w-8 -mt-1"
                    onClick={() => removeItem(item.productId)}
                    aria-label={`Remover ${item.name}`}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
                <p className="text-xs font-light text-primary-600 dark:text-primary-400">
                  {formatBrlCurrency(item.priceCents)} cada
                  {item.quantity > 1 && (
                    <>
                      {" "}
                      · {formatBrlCurrency(lineTotalCents)}
                    </>
                  )}
                </p>
                <QuantityStepper
                  productId={item.productId}
                  name={item.name}
                  quantity={item.quantity}
                />
              </div>
            </li>
          );
        })}
      </ul>
      <p className="text-sm font-light text-primary-900 dark:text-white">
        Total aproximado:{" "}
        <span className="font-normal">{formatBrlCurrency(totalCents)}</span>
      </p>
    </div>
  );
}
